/**
 * Types for MarketFit Guided Flow: intent, assumptions, ICP.
 */

export const MARKETFIT_INTENT_TYPES = [
  'explore',
  'validate',
  'prioritize',
  'diagnose',
] as const;
export type MarketFitIntentType = (typeof MARKETFIT_INTENT_TYPES)[number];

export const CONFIDENCE_BIAS_OPTIONS = ['open', 'neutral', 'skeptical'] as const;
export type ConfidenceBias = (typeof CONFIDENCE_BIAS_OPTIONS)[number];

export interface MarketFitIntent {
  type: MarketFitIntentType;
  confidence_bias?: ConfidenceBias;
}

export const ASSUMPTION_ELEMENT_TYPES = [
  'situation_constraint',
  'actor',
  'economic_value',
  'emotional_delta',
  'commitment',
] as const;
export type AssumptionElementType = (typeof ASSUMPTION_ELEMENT_TYPES)[number];

export const ASSUMPTION_SOURCES = [
  'user_stated',
  'inferred',
  'agent_hypothesis',
] as const;
export type AssumptionSource = (typeof ASSUMPTION_SOURCES)[number];

export const VALIDATION_TYPES = [
  'founder_experience',
  'customer_interview',
  'observational',
  'usage_data',
  'market_research',
  'experiment',
  'none',
] as const;
export type ValidationType = (typeof VALIDATION_TYPES)[number];

export const CONFIDENCE_LEVELS = ['low', 'medium', 'high', 'verified'] as const;
export type ConfidenceLevel = (typeof CONFIDENCE_LEVELS)[number];

export interface AssumptionEntry {
  id: string;
  element_type: AssumptionElementType;
  description: string;
  source: AssumptionSource;
  validation_type: ValidationType;
  confidence_level: ConfidenceLevel;
  source_step?: string; // state_id when created, for validation dropdown targeting
}

/** User-friendly validation options for the inline dropdown */
export const VALIDATION_DROPDOWN_OPTIONS: { value: ValidationType; label: string; confidence: ConfidenceLevel }[] = [
  { value: 'none', label: 'Hypothesis only', confidence: 'low' },
  { value: 'founder_experience', label: 'Based on experience', confidence: 'high' },
  { value: 'customer_interview', label: 'Based on interviews', confidence: 'high' },
  { value: 'observational', label: 'Based on data', confidence: 'medium' },
  { value: 'experiment', label: 'Experimentally verified', confidence: 'verified' },
];

export interface ICPSummary {
  primary_actor: {
    role: string;
    authority_scope: string;
    emotional_drivers: string[];
    risk_profile: string;
  };
  firmographic_filters: {
    industry: string;
    size_range: string;
    operational_trigger: string;
  };
  messaging: {
    constraint_framing: string;
    emotional_hook: string;
    economic_justification: string;
  };
}

export interface MarketFitScoredOutput {
  score?: number;
  interpretation_band?: string;
  interpretation_label?: string;
  confidence_weight?: number;
  weighted_marketfit_score?: number;
  svp?: number;
  acr?: number;
  ce?: number;
  icp_summary?: ICPSummary | null;
}
